import type { AdvancedFilterQuery } from '@packages/types';
import constate from 'constate';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useFilterMetadataAndValues } from './useFilterMetadataAndValues';
import { useMultiSelectState } from './useMultiSelectState';
import { useSearchTerms } from './useSearchTerms';
import type { ActiveFilter } from '../types/filter-metadata.types';
import type { FilterCondition, FilterGroup, GroupOperator } from '../types/filter-panel.types';
import { mapDefaultOperator } from '../types/filter-panel.types';

interface PatientFiltersStateProps {
    initialAdvanced?: AdvancedFilterQuery;
    initialFilters?: ActiveFilter[];
}

/**
 * Creates initial filter groups from external data
 */
const createInitialGroups = (initialAdvanced?: AdvancedFilterQuery, initialFilters?: ActiveFilter[]): FilterGroup[] => {
    if (initialAdvanced?.groups?.length) {
        return initialAdvanced.groups.map((group, idx) => ({
            id: `${idx + 1}`,
            operator: (group.operator || 'AND') as GroupOperator,
            conditions: group.conditions.map((c, cIdx) => ({
                id: `${idx + 1}-${cIdx + 1}`,
                field: c.field,
                operator: c.operator as FilterCondition['operator'],
                value: c.value,
                conditionOperator: (c as any).conditionOperator as GroupOperator | undefined,
            })),
        }));
    }

    if (initialFilters?.length) {
        const conditions: FilterCondition[] = initialFilters.map((f, idx) => ({
            id: `${Date.now()}-${idx}`,
            field: f.field,
            operator: f.operator as FilterCondition['operator'],
            value: f.value,
        }));
        return [{ id: '1', operator: 'AND', conditions }];
    }

    // Default: return one group with one condition
    return [
        {
            id: '1',
            operator: 'AND',
            conditions: [{ id: '1-1', field: 'search' }],
        },
    ];
};

function usePatientFiltersState({ initialAdvanced, initialFilters }: PatientFiltersStateProps = {}) {
    // Search and metadata
    const { searchTerms, handleSearch: handleSearchTerm } = useSearchTerms();

    // Multi-select state
    const {
        fieldLabels,
        multiSelectInValues,
        multiSelectOutValues,
        multiSelectOperator,
        setFieldLabels,
        setMultiSelectInValues,
        setMultiSelectOutValues,
        setMultiSelectOperator,
    } = useMultiSelectState();

    // Filter groups state
    const initialGroups = useMemo(
        () => createInitialGroups(initialAdvanced, initialFilters),
        [initialAdvanced, initialFilters]
    );
    const [filterGroups, setFilterGroups] = useState<FilterGroup[]>(initialGroups);

    const { metadata, filterValuesMap, diagnosisGroupedOptions, groupedFieldOptions } = useFilterMetadataAndValues(
        searchTerms,
        filterGroups
    );
    const [showGroupOperatorSelection, setShowGroupOperatorSelection] = useState(false);

    // Refs
    const containerRef = useRef<HTMLDivElement>(null);
    const lastAddedGroupRef = useRef<string | null>(null);
    const lastAddedConditionRef = useRef<{ groupId: string; conditionId: string } | null>(null);

    /**
     * Cleans up multi-select state for a specific condition
     */
    const cleanupConditionState = useCallback(
        (conditionKey: string) => {
            setFieldLabels(prev => {
                const newState = { ...prev };
                delete newState[conditionKey];
                return newState;
            });
            setMultiSelectInValues(prev => {
                const newState = { ...prev };
                delete newState[conditionKey];
                return newState;
            });
            setMultiSelectOutValues(prev => {
                const newState = { ...prev };
                delete newState[conditionKey];
                return newState;
            });
        },
        [setFieldLabels, setMultiSelectInValues, setMultiSelectOutValues]
    );

    // Filter group operations
    const updateCondition = useCallback(
        (groupId: string, conditionId: string, updates: Partial<FilterCondition>) => {
            if (updates.field !== undefined) {
                cleanupConditionState(`${groupId}-${conditionId}`);
            }
            setFilterGroups(groups =>
                groups.map(group =>
                    group.id === groupId
                        ? {
                              ...group,
                              conditions: group.conditions.map(c => (c.id === conditionId ? { ...c, ...updates } : c)),
                          }
                        : group
                )
            );
        },
        [cleanupConditionState]
    );

    const clearFilters = useCallback((onFiltersChange?: (filters: ActiveFilter[]) => void) => {
        setFilterGroups([
            {
                id: `${Date.now()}`,
                operator: 'AND',
                conditions: [{ id: `${Date.now()}-1`, field: 'search' }],
            },
        ]);
        setShowGroupOperatorSelection(false);
        onFiltersChange?.([]);
    }, []);

    const addFilterGroup = useCallback((groupOperator?: GroupOperator) => {
        const newGroupId = `${Date.now()}`;
        lastAddedGroupRef.current = newGroupId;
        setFilterGroups(groups => [...groups, { id: newGroupId, operator: groupOperator || 'AND', conditions: [] }]);
        setShowGroupOperatorSelection(false);
    }, []);

    const removeFilterGroup = useCallback((groupId: string) => {
        setFilterGroups(groups => groups.filter(g => g.id !== groupId));
    }, []);

    const toggleGroupOperator = useCallback((groupId: string) => {
        setFilterGroups(groups =>
            groups.map(g => (g.id === groupId ? { ...g, operator: g.operator === 'AND' ? 'OR' : 'AND' } : g))
        );
    }, []);

    const addCondition = useCallback((groupId: string, conditionOperator?: GroupOperator) => {
        const newConditionId = `${Date.now()}`;
        lastAddedConditionRef.current = { groupId, conditionId: newConditionId };
        setFilterGroups(groups => {
            const group = groups.find(g => g.id === groupId);
            const isFirstCondition = !group || group.conditions.length === 0;
            return groups.map(g =>
                g.id === groupId
                    ? {
                          ...g,
                          conditions: [
                              ...g.conditions,
                              {
                                  id: newConditionId,
                                  field: 'search',
                                  ...(isFirstCondition ? {} : { conditionOperator: conditionOperator || 'AND' }),
                              },
                          ],
                      }
                    : g
            );
        });
    }, []);

    const removeCondition = useCallback(
        (groupId: string, conditionId: string) => {
            cleanupConditionState(`${groupId}-${conditionId}`);
            setFilterGroups(groups =>
                groups.map(g =>
                    g.id === groupId ? { ...g, conditions: g.conditions.filter(c => c.id !== conditionId) } : g
                )
            );
        },
        [cleanupConditionState]
    );

    return {
        // Metadata & search
        metadata,
        filterValuesMap,
        diagnosisGroupedOptions,
        groupedFieldOptions,
        handleSearchTerm,

        // Multi-select state
        fieldLabels,
        multiSelectInValues,
        multiSelectOutValues,
        multiSelectOperator,
        setFieldLabels,
        setMultiSelectInValues,
        setMultiSelectOutValues,
        setMultiSelectOperator,

        // Filter groups state
        filterGroups,
        setFilterGroups,
        showGroupOperatorSelection,
        setShowGroupOperatorSelection,
        containerRef,

        // Filter group operations
        updateCondition,
        clearFilters,
        addFilterGroup,
        removeFilterGroup,
        toggleGroupOperator,
        addCondition,
        removeCondition,

        // Utility
        mapDefaultOperator,
    };
}

// Export constate provider and hooks
export const [
    PatientFiltersProvider,
    // Metadata hooks
    useFilterMetadata,
    useFilterOptions,
    // Multi-select hooks
    useMultiSelectValues,
    useMultiSelectActions,
    // Filter groups hooks
    useFilterGroupsState,
    useFilterGroupsActions,
    // Utility
    useMapDefaultOperator,
] = constate(
    usePatientFiltersState,
    // Metadata
    value => ({
        metadata: value.metadata,
        filterValuesMap: value.filterValuesMap,
        handleSearchTerm: value.handleSearchTerm,
    }),
    // Field options
    value => ({
        groupedFieldOptions: value.groupedFieldOptions,
        diagnosisGroupedOptions: value.diagnosisGroupedOptions,
    }),
    // Multi-select values
    value => ({
        multiSelectInValues: value.multiSelectInValues,
        multiSelectOutValues: value.multiSelectOutValues,
        multiSelectOperator: value.multiSelectOperator,
    }),
    // Multi-select actions
    value => ({
        setFieldLabels: value.setFieldLabels,
        setMultiSelectInValues: value.setMultiSelectInValues,
        setMultiSelectOutValues: value.setMultiSelectOutValues,
        setMultiSelectOperator: value.setMultiSelectOperator,
    }),
    // Filter groups state
    value => ({
        filterGroups: value.filterGroups,
        setFilterGroups: value.setFilterGroups,
        showGroupOperatorSelection: value.showGroupOperatorSelection,
        setShowGroupOperatorSelection: value.setShowGroupOperatorSelection,
        containerRef: value.containerRef,
    }),
    // Filter groups actions
    value => ({
        updateCondition: value.updateCondition,
        clearFilters: value.clearFilters,
        addFilterGroup: value.addFilterGroup,
        removeFilterGroup: value.removeFilterGroup,
        toggleGroupOperator: value.toggleGroupOperator,
        addCondition: value.addCondition,
        removeCondition: value.removeCondition,
    }),
    // Utility
    value => value.mapDefaultOperator
);
